import React, { useLayoutEffect, useRef } from 'react';
import * as StatsActions from '../../actions/StatsActions'

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { start } from 'repl';
import { first } from '@amcharts/amcharts4/.internal/core/utils/Array';

am4core.useTheme(am4themes_dark);
am4core.useTheme(am4themes_animated);

export interface IYearlyRadarChartProps {
    id: string;
    xAxisTitle: string;
    yAxisTitle: string;
    data: Array<StatsActions.IDateValueStatisticsType>
  }

const YearlyRadarChart: React.FC<IYearlyRadarChartProps> = (props: IYearlyRadarChartProps) => {
    const chart = useRef();

    useLayoutEffect(() => {
        let data: any = [];
        let startYear = 1000000;
        let endYear = 0;
        let currentYear = 2018;

        // Set the current year to the max year in the data.
        for (var i = 0; i < props.data.length; i++) {
            let entry = props.data[i];
            let localDate = new Date(entry.date);
            if (localDate.getFullYear() > currentYear) {
                currentYear = localDate.getFullYear();
            }
        }

        function setData() {
            let dataIndex = 0;
            data.splice(0, data.length);
            for (var i = 0; i < props.data.length; i++) {
                let entry = props.data[i];
                let localDate = new Date(entry.date);

                // Get the number of hours offset for the current local timezone.
                let timeZoneHoursOffset = localDate.getTimezoneOffset() / 60;
                localDate.setHours(localDate.getHours() + timeZoneHoursOffset);

                let year = localDate.getFullYear();

                if (year < startYear) {
                    startYear = year;
                }

                if (year > endYear) {
                    endYear = year;
                }

                if (year != currentYear) {
                    continue;
                }

                data[dataIndex] = {};
                data[dataIndex]["Activity Date"] = localDate;
                data[dataIndex]["Activity Name"] = `${entry.value} charts played`;
                data[dataIndex]["Activity Type"] = "Dance Games";
                data[dataIndex]["PlayCount"] = entry.value;
                dataIndex++;
            }

            data.sort((a: any, b: any) => 
            {
                let aDate: Date = a["Activity Date"] as Date;
                let bDate: Date = b["Activity Date"] as Date;

                if (aDate > bDate) {
                    return -1;
                }
                else {
                    return 1;
                }
            });
        }

        setData();
        currentYear = endYear;

        // CHART
        let c = am4core.create(`${props.id}`, am4charts.RadarChart);

        let weeklyData: any = [];
        let dailyData: any = [];

        let firstDay = new Date(data[0]["Activity Date"]);
        firstDay = am4core.time.round(firstDay, "year", 1);
        let totalYearlyPlays = 0;

        // PREPARE DATA
        
        for (var i = 0; i < 7; i++) {
            // Start the week on Monday so the bubbles are ordered properly.
            dailyData.push({ day: (c.dateFormatter.language as any).translate(c.dateFormatter.weekdaysShort[(i + 1) % 7]) });
        }

        function getWeekNumber(date: Date) {
            for (let i = 0; i < 53; ++i) {
                let week = weeklyData[i];
                if (date >= week.startDate && date < week.endDate) {
                    return i;
                }
            }

            return -1;
        }

        function getFestiveEmoji(date: Date) {
            if (date.getMonth() == 0 && date.getDate() == 1) {
                return '🍾';
            }
            else if (date.getMonth() == 1 && date.getDate() == 14) {
                return '💞';
            }
            else if (date.getMonth() == 2 && date.getDate() == 17) {
                return '🍀';
            }
            else if (date.getMonth() == 6 && date.getDate() == 4) {
                return '🎆';
            }
            else if (date.getMonth() == 9 && date.getDate() == 31) {
                return '🎃';
            }
            else if (date.getMonth() == 10 && date.getDate() == 14) {
                return '🕯️';
            }
            else if (date.getMonth() == 11 && date.getDate() == 25) {
                return '🎅';
            }

            return null;
        }

        function setWeeklyData() {
            firstDay = new Date(data[0]["Activity Date"]);
            firstDay = am4core.time.round(firstDay, "year", 1);

            weeklyData.splice(0, weeklyData.length);

            // Build the weeks array.
            let firstMonday = new Date(firstDay);
            firstMonday.setDate(-firstMonday.getDay() + 2);
            for (var i = 0; i < 53; i++) {
                weeklyData[i] = {};
                weeklyData[i].playCount = 0;
                
                let startDate = new Date(firstMonday);
                startDate.setDate(startDate.getDate() + (i * 7));
                let endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 7);
                let displayEndDate = new Date(startDate);
                displayEndDate.setDate(displayEndDate.getDate() + 6);

                weeklyData[i].startDate = startDate;
                weeklyData[i].endDate = endDate;
                weeklyData[i].displayEndDate = displayEndDate;
            }
        }

        setWeeklyData();

        function setPlayCounts() {
            dailyData.splice(0, dailyData.length);

            for (var i = 0; i < 7; i++) {
                // Start the week on Monday so the bubbles are ordered properly.
                dailyData.push({ day: (c.dateFormatter.language as any).translate(c.dateFormatter.weekdaysShort[(i + 1) % 7]) });
            }

            // Fill in the total play counts for each week.
            let dailyDataIndex = 7;
            for (var i = 0; i < data.length; i++) {
                let entry = data[i];
                let date = new Date(entry["Activity Date"]);
                let weekDay = date.getDay();
                let playCount = entry["PlayCount"];

                let weekNumber = getWeekNumber(date);

                if (weekNumber == -1) {
                    continue;
                }

                let longDate: string = date.toLocaleString(
                    'en-US',
                    {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                    });

                let emoji = getFestiveEmoji(date);
                if (emoji != null) {
                    longDate = `${longDate} ${emoji}`
                }

                weeklyData[weekNumber].playCount += playCount;
                totalYearlyPlays += playCount;

                dailyData[dailyDataIndex] = {};
                dailyData[dailyDataIndex].date = date;
                dailyData[dailyDataIndex].longDate = longDate;
                dailyData[dailyDataIndex].day = (c.dateFormatter.language as any).translate(c.dateFormatter.weekdaysShort[weekDay]);
                dailyData[dailyDataIndex].playCount = playCount;
                dailyData[dailyDataIndex].title = entry["Activity Name"];
                dailyDataIndex++;
            }
        }

        setPlayCounts();

        let yearTitle = c.createChild(am4core.Label);
        yearTitle.fill = am4core.color("#7D00F5");
        yearTitle.fontSize = 40;
        yearTitle.isMeasured = true;
        yearTitle.valign = "bottom";
        yearTitle.align = "right";
        yearTitle.wrap = false;
        yearTitle.width = 100;
        yearTitle.text = currentYear.toString();

        chart.current = c as any;

        c.innerRadius = am4core.percent(15);
        c.radius = am4core.percent(90);
        c.data = weeklyData;
        c.fontSize = "11px";
        c.startAngle = 95;
        c.endAngle = c.startAngle + 350;

        // Create axes
        let dateAxis = c.xAxes.push(new am4charts.DateAxis() as any);
        dateAxis.baseInterval = { timeUnit: "week", count: 1 };
        dateAxis.renderer.innerRadius = am4core.percent(40);
        dateAxis.renderer.minGridDistance = 5;
        dateAxis.renderer.labels.template.relativeRotation = 0;
        dateAxis.renderer.labels.template.location = 0.5;
        dateAxis.renderer.labels.template.radius = am4core.percent(-57);
        dateAxis.renderer.labels.template.fontSize = "8px";
        dateAxis.dateFormats.setKey("week", "w");
        dateAxis.periodChangeDateFormats.setKey("week", "w");
        dateAxis.cursorTooltipEnabled = false;

        let valueAxis = c.yAxes.push(new am4charts.ValueAxis() as any);
        valueAxis.renderer.inversed = true;
        valueAxis.renderer.radius = am4core.percent(40);
        valueAxis.renderer.minGridDistance = 15;
        valueAxis.renderer.minLabelPosition = 0.05;
        valueAxis.renderer.axisAngle = 90;
        valueAxis.cursorTooltipEnabled = false;
        valueAxis.renderer.labels.template.fill = am4core.color("#ffffff");
        valueAxis.extraMin = 0;

        // Create series for the weekly total columns.
        let columnSeries = c.series.push(new am4charts.RadarColumnSeries() as any);
        columnSeries.dataFields.dateX = "startDate";
        columnSeries.dataFields.valueY = "playCount";
        columnSeries.columns.template.strokeOpacity = 0;
        columnSeries.columns.template.width = am4core.percent(95);
        columnSeries.fill = am4core.color("#058AE5");
        columnSeries.fillOpacity = 0.6;
        columnSeries.tooltip.fontSize = 10;
        columnSeries.tooltip.pointerOrientation = "down";
        columnSeries.tooltip.background.fillOpacity = 0.5;
        columnSeries.columns.template.tooltipText = "[bold]{startDate} - {displayEndDate}\n[font-size:13px] {valueY} charts played";
        columnSeries.cursorTooltipEnabled = false;

        // weekday axis
        let weekDayAxis = c.yAxes.push(new am4charts.CategoryAxis() as any);
        weekDayAxis.dataFields.category = "day";
        weekDayAxis.data = dailyData;
        weekDayAxis.renderer.innerRadius = am4core.percent(50);
        weekDayAxis.renderer.minGridDistance = 10;
        weekDayAxis.renderer.grid.template.location = 0;
        weekDayAxis.renderer.line.disabled = true;
        weekDayAxis.renderer.axisAngle = 90;
        weekDayAxis.cursorTooltipEnabled = false;
        weekDayAxis.renderer.labels.template.fill = am4core.color("#ffffff");

        // bubble series
        let bubbleSeries: any = c.series.push(new am4charts.RadarSeries())
        bubbleSeries.dataFields.dateX = "date";
        bubbleSeries.dataFields.categoryY = "day";
        bubbleSeries.dataFields.value = "playCount";
        bubbleSeries.yAxis = weekDayAxis;
        bubbleSeries.data = dailyData;
        bubbleSeries.strokeOpacity = 0;
        bubbleSeries.maskBullets = false;
        bubbleSeries.cursorTooltipEnabled = false;
        bubbleSeries.tooltip.fontSize = 10;
        bubbleSeries.tooltip.pointerOrientation = "down";
        bubbleSeries.tooltip.background.fillOpacity = 0.4;

        let bubbleBullet = bubbleSeries.bullets.push(new am4charts.CircleBullet())
        bubbleBullet.locationX = 0.5;
        bubbleBullet.stroke = am4core.color("#390072");
        bubbleBullet.fill = am4core.color("#7D00F5");
        bubbleBullet.tooltipText = "[bold]{longDate}\n[font-size:13px]{title}";
        bubbleBullet.adapter.add("tooltipY", function(tooltipY: any, target: any) {
        return -target.circle.radius;
        })

        bubbleSeries.heatRules.push({ target: bubbleBullet.circle, min: 2, max: 12, dataField: "value", property: "radius" });
        bubbleSeries.dataItems.template.locations.categoryY = 0.5;

        function setMonthRanges() {
            dateAxis.axisRanges.clear();

            // add month ranges
            for (var i = 0; i < 13; i++) {
                let range = dateAxis.axisRanges.create();
                range.date = new Date(firstDay.getFullYear(), i, 0, 0, 0, 0);
                range.endDate = new Date(firstDay.getFullYear(), i + 1, 0, 0, 0, 0);
                if (i % 2) {
                    range.axisFill.fillOpacity = 0.4;
                }
                else {
                    range.axisFill.fillOpacity = 0.8;
                }
                range.axisFill.radius = -28;
                range.axisFill.adapter.add("innerRadius", function(innerRadius: any, target: any) {
                    return dateAxis.renderer.pixelRadius + 7;
                })
                range.axisFill.fill = am4core.color("#7D00F5");
                range.axisFill.stroke = am4core.color("#390072");
                range.grid.disabled = true;
                range.label.text = (c.dateFormatter.language as any).translate(c.dateFormatter.months[i])
                range.label.bent = true;
                range.label.radius = 10;
                range.label.fontSize = 10;
                range.label.paddingBottom = 5;
                range.label.interactionsEnabled = false;
                range.axisFill.interactionsEnabled = true;
                range.axisFill.cursorOverStyle = am4core.MouseCursorStyle.pointer;
                range.axisFill.events.on("hit", function(event: any) {
                    if (dateAxis.start == 0 && dateAxis.end == 1) {
                    dateAxis.zoomToDates(event.target.dataItem.date, event.target.dataItem.endDate);
                    }
                    else {
                    dateAxis.zoom({ start: 0, end: 1 });
                    }
                })
            }
        }

        setMonthRanges();

        c.cursor = new am4charts.RadarCursor();
        c.cursor.innerRadius = am4core.percent(40);
        c.cursor.lineY.disabled = true;


        let label = c.radarContainer.createChild(am4core.Label);
        label.horizontalCenter = "middle";
        label.verticalCenter = "middle";
        label.fill = am4core.color("#ffffff");
        label.fontSize = 12;
        label.fontWeight = "bold";
        label.text = `WEEKLY\nTOTALS`;

        let title = c.createChild(am4core.Label);
        title.fill = am4core.color("#7D00F5");
        title.fontSize = 20;
        title.isMeasured = false;
        title.valign = "top";
        title.align = "left";
        title.wrap = true;
        title.width = 300;
        title.text = "[bold]In " + currentYear + ",\nyou played " + totalYearlyPlays.toLocaleString() + " charts.\n[font-size:11; #ffffff]Each circle represents a day played. Size represents number of charts played. If you've played for multiple years, the slider at the bottom can change the year.";
    
        // year slider
        let yearSliderContainer = c.createChild(am4core.Container);
        yearSliderContainer.layout = "vertical";
        yearSliderContainer.padding(0, 38, 0, 38);
        yearSliderContainer.width = am4core.percent(100);

        function updateChartData() {
            c.data.splice(0, c.data.length);
            for (let i = 0; i < weeklyData.length; i++) {
                c.data[i].playCount = weeklyData[i].playCount;
                c.data[i].startDate = weeklyData[i].startDate;
                c.data[i].endDate = weeklyData[i].endDate;
                c.data[i].displayEndDate = weeklyData[i].displayEndDate;
            }
        }

        function updateMonthRanges() {
            for (let i = 0; i < dateAxis.axisRanges.length; i++) {
                let range = dateAxis.axisRanges[i];
                range.date = new Date(firstDay.getFullYear(), i, 0, 0, 0, 0);
                range.endDate = new Date(firstDay.getFullYear(), i + 1, 0, 0, 0, 0);
            }
        }

        function updateRadarData() {
            totalYearlyPlays = 0;
            
            setData();
            setWeeklyData();
            setPlayCounts();

            setMonthRanges();

            weekDayAxis.data = dailyData;
            bubbleSeries.data = dailyData;
            c.data = weeklyData;

            title.text = "[bold]In " + currentYear + ",\nyou played " + totalYearlyPlays.toLocaleString() + " charts.\n[font-size:11; #ffffff]Each circle represents a day played. Size represents number of charts played. If you've played for multiple years, the slider at the bottom can change the year.";
            yearTitle.text = currentYear.toString();
        }

        let yearSlider = yearSliderContainer.createChild(am4core.Slider);
        yearSlider.events.on("rangechanged", function () {
            let newCurrentYear = startYear + Math.round(yearSlider.start * (endYear - startYear));
            if (newCurrentYear == currentYear) {
                return;
            }

            currentYear = newCurrentYear;
            updateRadarData();
        })
        yearSlider.orientation = "horizontal";
        yearSlider.start = 1;
        yearSlider.exportable = false;

        return () => {
            c.dispose();
        };
      }, [props]);
    
    return (
        <div className="YearlyRadarChart" id={props.id} style={{ width: "100%", height: "1200px" }}></div>
    );
  }
  
  export default YearlyRadarChart;